import { useCIDR, useEntriesV2, useTimeZoneConverter } from "@/Hooks"
import { differenceInMilliseconds, parseISO } from "date-fns"
import { useEffect, useReducer } from "react"
import { useParams } from "react-router-dom"
import { initialState, reducer } from "../reducers"

export const useAddSubnetMappingModal = (directTunnelData, getDirectTunnelSubnetMapping) => {
	const { id } = useParams()
	const { convertToServerTimeZone } = useTimeZoneConverter()
	const { validateAddress, validatePrefix, validateCIDR, validateCIDRAddress } = useCIDR()
	const [state, dispatch] = useReducer(reducer, initialState)

	const [directTunnelUserMappings, { getData: getDirectTunnelUserMappingsData }] = useEntriesV2(`/api/v2/device/directTunnel/${id}/userMapping`)
	const [addDirectTunnelSubnetMappingStates, { postData: addDirectTunnelSubnetMapping, reset: resetAddDTS }] = useEntriesV2(
		`/api/v2/device/directTunnel/${id}/SubnetMapping`,
		null,
		(errorResponse) => {
			if (errorResponse?.code === 10) throw new Error("A similar Subnet Mapping associated with this user already exists")
		}
	)

	/** Converters */
	/** convert the user data from parent component to user list in the modal
	 * @param {Array} data - fetched user data
	 * @returns {Array} - user options for display in the modal
	 */
	const convertUserMappingsDataToOptions = (data) => {
		return (
			data &&
			data.map((user) => ({
				key: user.user_id,
				value: user.email,
				text: user.email,
				peers: user.peer_minimum
			}))
		)
	}

	const validationTimeRange = () => {
		const { activated_time, deactivated_time } = state.requestData
		const MIN_TIME_RANGE = 5 * 60 * 1000 // 5 minutes converted to milliseconds
		const activated = parseISO(activated_time)
		const deactivated = parseISO(deactivated_time)
		return differenceInMilliseconds(deactivated, activated) >= MIN_TIME_RANGE
	}

	/** Handlers */
	/** Handler open modal */
	const handleModalOpen = () => {
		dispatch({ type: "OPEN_ADD_SUBNET_MAPPING_MODAL" })
	}
	/** handle choose the user with peers */
	const handleChooseUser = (data) => {
		const user = state.userMappings.find((user) => user.email == data.value)
		dispatch({
			type: "HANDLE_ON_USER_CHANGE",
			payload: {
				user: {
					"auth0_user_id": user.user_id,
					"user_name": "",
					"email": user.email,
					"peers": user.peer_minimum.map((peer) => peer.name)
				},
				peers: user.peer_minimum.map((peer) => peer.name)
			}
		})
	}

	/**
	 * Handle input subnet address.
	 * Validates the address with or without the prefix and updates the state accordingly.
	 * @param {string} value - Subnet address
	 * @returns {void}
	 */
	const handleAddressInput = (value) => {
		// Check the address format is legal.
		if (validateAddress(value)) {
			dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: false })
		} else {
			dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: true })
		}

		// If the address is valid and a prefix exists, revalidate the prefix with the new address.
		if (state.prefix) {
			// Validate the address with the prefix and check if the address is the start of the CIDR.
			if (validateCIDR(value, state.prefix) && validateCIDRAddress(value, state.prefix)) {
				dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: false })
				dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: false })
			} else {
				dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: true })
				dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: true })
			}
		}

		dispatch({ type: "HANDLE_INPUT_ADDRESS", payload: value })
	}

	/**
	 * Handle input subnet prefix.
	 * Validates the prefix with or without the address and updates the state accordingly.
	 * @param {string} value - Subnet prefix
	 * @returns {void}
	 */
	const handlePrefixInput = (value) => {
		// Check the prefix format is legal.
		if (validatePrefix(value)) {
			dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: false })
		} else {
			dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: true })
		}

		// If the prefix is valid and an address exists, revalidate the address with the new prefix.
		if (state.address) {
			// Validate the prefix with the address and check if the address is the start of the CIDR.
			if (validateCIDR(state.address, value) && validateCIDRAddress(state.address, value)) {
				dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: false })
				dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: false })
			} else {
				dispatch({ type: "IS_NOT_VALIDATED_PREFIX", payload: true })
				dispatch({ type: "IS_NOT_VALIDATED_ADDRESS", payload: true })
			}
		}

		dispatch({ type: "HANDLE_INPUT_PREFIX", payload: value })
	}

	/** handle input reason */
	const handleInputReason = (event) => {
		dispatch({ type: "HANDLE_INPUT_REASON", payload: event.target.value })
	}
	/** handle switch mode */
	const handleSwitchMode = (value) => {
		dispatch({ type: "HANDLE_SWITCH_MODE", payload: value })
		// when the mode is permanent, clear the time value in the request data as empty string.
		if (value === "permanent") {
			dispatch({ type: "HANDLE_SET_TIME_BASE", payload: [null, null] })
		}
	}
	/** handle set time base */
	const handleSetTimeBase = (startTime, endTime) => {
		// even the default value is timebased, but the request data mode is not exist, so need to add the mode to timebased.
		dispatch({ type: "HANDLE_SET_TIME_BASE", payload: [convertToServerTimeZone(startTime), convertToServerTimeZone(endTime)] })
		handleSwitchMode("scheduled")
	}

	/** handle reset time range validation */
	const handleResetTimeRangeValidation = () => {
		dispatch({ type: "IS_NOT_VALIDATED_TIME_RANGE", payload: false })
	}

	/** Handle confirm */
	const handleConfirm = () => {
		const { requestData, address, prefix, isNotValidatedAddress, isNotValidatedPrefix } = state

		if (!requestData?.user_peer_col) {
			dispatch({ type: "IS_NOT_CHOOSE_USER" })
			return
		}
		if (!address) {
			dispatch({ type: "IS_NOT_INPUT_ADDRESS" })
			return
		}
		if (!prefix) {
			dispatch({ type: "IS_NOT_INPUT_PREFIX" })
			return
		}

		if (!requestData?.mode) {
			dispatch({ type: "IS_NOT_INPUT_TIME_BASE" })
			return
		}

		// Validate time range if mode is "Scheduled"
		if (requestData.mode === "Scheduled") {
			const { activated_time, deactivated_time } = requestData

			if (!activated_time || !deactivated_time) {
				dispatch({ type: "IS_NOT_INPUT_TIME_BASE" })
				return
			}

			if (!validationTimeRange()) {
				dispatch({ type: "IS_NOT_VALIDATED_TIME_RANGE", payload: true })
				return
			}
		}

		// Prevent submission if address or prefix validation fails
		if (isNotValidatedAddress || isNotValidatedPrefix) {
			return
		}

		// Set status based on mode
		const status = requestData.mode === "Permanent"

		addDirectTunnelSubnetMapping({
			...requestData,
			status,
			network_mapping: `${address}/${prefix}`
		})
	}
	/** Handle cancel or close error message */
	const handleCancel = () => {
		dispatch({ type: "RESET_All" })
		resetAddDTS()
	}

	useEffect(() => {
		getDirectTunnelUserMappingsData()
	}, [])

	useEffect(() => {
		if (directTunnelUserMappings.data) {
			dispatch({
				type: "GET_USER_MAPPINGS",
				payload: {
					userMappings: directTunnelUserMappings.data,
					userOptions: convertUserMappingsDataToOptions(directTunnelUserMappings.data)
				}
			})
		}
	}, [directTunnelUserMappings.data])

	useEffect(() => {
		if (addDirectTunnelSubnetMappingStates.isSuccess) {
			getDirectTunnelSubnetMapping()
			dispatch({ type: "RESET_All" })
		}
	}, [addDirectTunnelSubnetMappingStates.isSuccess])

	return {
		state,
		directTunnelUserMappings,
		addDirectTunnelSubnetMappingStates,
		handleModalOpen,
		handleChooseUser,
		handleAddressInput,
		handlePrefixInput,
		handleInputReason,
		handleSwitchMode,
		handleConfirm,
		handleCancel,
		handleSetTimeBase,
		handleResetTimeRangeValidation
	}
}
